/**
 * Manifest-driven game registry.
 * JSON manifests in current/games/*.json are the source of truth; loaders are
 * keyed by routeSlug for Vite-friendly static dynamic imports.
 */
import type {
  CoreManifest,
  GameAssetModule,
  GameManifest,
} from '@platform/contracts';
import type {
  AppGameControllerModule,
  AppGameRegistryEntry,
} from './game-registry-types';

import coreManifest from '../../current/core.json';
import crashManifest from '../../current/games/crash.json';
import skyXManifest from '../../current/games/sky-x.json';
import skyxManifest from '../../current/games/skyx.json';
import skyxNewManifest from '../../current/games/skyx_new.json';
import skySnifferManifest from '../../current/games/sky-sniffer.json';
import skySnifferV2Manifest from '../../current/games/sky-sniffer-v2.json';
import superdogManifest from '../../current/games/superdog.json';
import caveCrasherManifest from '../../current/games/cave-crasher.json';
import chickenluckManifest from '../../current/games/chickenluck.json';
import citySnifferManifest from '../../current/games/city-sniffer.json';
import nflKickManifest from '../../current/games/nflkick.json';

const gameManifests: Record<string, GameManifest> = {
  crash: crashManifest as GameManifest,
  'sky-x': skyXManifest as GameManifest,
  skyx: skyxManifest as GameManifest,
  skyx_new: skyxNewManifest as GameManifest,
  'sky-sniffer': skySnifferManifest as GameManifest,
  'sky-sniffer-v2': skySnifferV2Manifest as GameManifest,
  superdog: superdogManifest as GameManifest,
  'cave-crasher': caveCrasherManifest as GameManifest,
  chickenluck: chickenluckManifest as GameManifest,
  'city-sniffer': citySnifferManifest as GameManifest,
  nflkick: nflKickManifest as GameManifest,
};

/** Static import map required for Vite bundle analysis. */
const controllerLoaders: Record<
  string,
  () => Promise<AppGameControllerModule>
> = {
  crash: () => import('@games/crash/controller/CrashGameController'),
  'sky-x': () => import('@games/skyx_new/controller/CrashGameController'),
  skyx: () => import('@games/skyx/controller/CrashGameController'),
  skyx_new: () => import('@games/skyx_new/controller/CrashGameController'),
  'sky-sniffer': () =>
    import('@games/skysniffer/controller/CrashGameController'),
  'sky-sniffer-v2': () =>
    import('@games/skysnifferv2/controller/CrashGameController'),
  superdog: () => import('@games/superdog/controller/CrashGameController'),
  'cave-crasher': () => import('@games/mining/controller/CrashGameController'),
  chickenluck: () =>
    import('@games/chickenroad/controller/CrashGameController'),
  'city-sniffer': () =>
    import('@games/citysniffer/controller/CrashGameController'),
  nflkick: () => import('@games/nflkick/controller/NflGameController'),
};

const assetLoaders: Record<string, () => Promise<GameAssetModule>> = {
  crash: () => import('@games/crash/assets/assets.manifest'),
  'sky-x': () => import('@games/skyx_new/assets/assets.manifest'),
  skyx: () => import('@games/skyx/assets/assets.manifest'),
  skyx_new: () => import('@games/skyx_new/assets/assets.manifest'),
  'sky-sniffer': () => import('@games/skysniffer/assets/assets.manifest'),
  'sky-sniffer-v2': () => import('@games/skysniffer/assets/assets.manifest'),
  superdog: () => import('@games/superdog/assets/assets.manifest'),
  'city-sniffer': () => import('@games/citysniffer/assets/assets.manifest'),
  nflkick: () => import('@games/nflkick/assets/assets.manifest'),
};

export function getCoreManifest(): CoreManifest {
  return coreManifest as CoreManifest;
}

export function getGameManifest(slug: string): GameManifest | null {
  return gameManifests[slug] ?? null;
}

export function listGameManifests(): GameManifest[] {
  return Object.values(gameManifests);
}

export function listEnabledGameManifests(): GameManifest[] {
  return listGameManifests().filter(m => m.enabled);
}

export function resolveControllerImport(slug: string): string | null {
  return getGameManifest(slug)?.controllerModule ?? null;
}

function manifestToRegistryEntry(manifest: GameManifest): AppGameRegistryEntry {
  const loadController = controllerLoaders[manifest.routeSlug];
  if (!loadController) {
    throw new Error(
      `[manifest-loader] No controller loader for routeSlug "${manifest.routeSlug}"`
    );
  }

  const entry: AppGameRegistryEntry = {
    routeSlug: manifest.routeSlug,
    audioSlug: manifest.audioSlug,
    loadController,
  };

  const loadAssets = assetLoaders[manifest.routeSlug];
  if (manifest.assetModule && loadAssets) {
    entry.loadAssets = loadAssets;
  }

  return entry;
}

/** Ensures every enabled manifest has a Vite static loader entry. */
export function assertManifestRegistryValid(): void {
  const enabled = listEnabledGameManifests();
  const missing: string[] = [];

  for (const m of enabled) {
    if (!controllerLoaders[m.routeSlug]) {
      missing.push(m.routeSlug);
    }
    if (m.assetModule && !assetLoaders[m.routeSlug]) {
      missing.push(`${m.routeSlug} (assets)`);
    }
  }

  if (missing.length > 0) {
    throw new Error(
      `[manifest-loader] Missing loader(s) for enabled manifest(s): ${missing.join(', ')}`
    );
  }

  const orphanLoaders = Object.keys(controllerLoaders).filter(
    routeSlug => !enabled.some(m => m.routeSlug === routeSlug)
  );
  if (orphanLoaders.length > 0) {
    console.warn(
      '[manifest-loader] Controller loaders without enabled manifest:',
      orphanLoaders.join(', ')
    );
  }
}

export function buildGameRegistry(): Record<string, AppGameRegistryEntry> {
  assertManifestRegistryValid();

  const registry: Record<string, AppGameRegistryEntry> = {};
  for (const manifest of listEnabledGameManifests()) {
    registry[manifest.routeSlug] = manifestToRegistryEntry(manifest);
  }
  return registry;
}
